// The profile wall's wire types.
//
// Hand-written rather than generated (AGENTS.md rule 6): the endpoints are new and regenerating the
// client needs a running local backend plus a devo tunnel. They mirror
// `MainService/.../data/model/dto/wall/ProfileWallEntryDTOImpl.java` and
// `ProfileWallService.ProfileWallPage`.
//
// ⚠️ There is NO compile-time link between these shapes and the server's, so a field renamed on one
// side is a silent `undefined` on the other. Keep them in step by hand, and prefer widening a field to
// `| null` over asserting it is present: half of them are legitimately null for half the kinds.
//
// Design: designs/00-inbox/2026-08-25-profile-wall-unified-activity-timeline-design.md §2, §5.

/**
 * The four kinds phase 2 ships.
 *
 * `COMMENT` is deliberately absent: it is phase 3 (free text, its own table, an owner-approval gate and
 * a moderation surface) and it is the operator's call to start it. `TOP_REPLY` is absent too — P7
 * deferred it pending the §2.3 A4 check.
 */
export type ProfileWallEntryKind = "ENDORSEMENT" | "ACHIEVEMENT" | "ANSWER_ACCEPTED" | "MIND_CHANGED";

export const PROFILE_WALL_ENTRY_KINDS: readonly ProfileWallEntryKind[] = [
    "ENDORSEMENT",
    "ACHIEVEMENT",
    "ANSWER_ACCEPTED",
    "MIND_CHANGED",
] as const;

export type ProfileWallEntry = {
    kind: ProfileWallEntryKind;
    /** ISO-8601. When the thing happened, never when the wall was read. */
    occurredAt: string;
    /**
     * The source row's key. It addresses the entry for removal and for the anchor; it authorises
     * nothing — the server re-checks ownership against the session.
     */
    sourceId: number;
    /** Null on ACHIEVEMENT, which has no artefact. Present on the other three (§2.3 A2). */
    discussionPublicId: string | null;
    thoughtPublicId: string | null;
    title: string | null;
    /** Null on ACHIEVEMENT: the system asserted it, so there is no actor to name. */
    actorUsername: string | null;
    /** ENDORSEMENT: the closed-vocabulary kind. ACHIEVEMENT: the family key. Else null. */
    detail1: string | null;
    /** ENDORSEMENT: the note, or null. ACHIEVEMENT: the tier, as text. Else null. */
    detail2: string | null;
    /** Whether THIS viewer may remove THIS entry. Never trust it as authorisation; it is an affordance. */
    viewerCanRemove: boolean;
};

export type ProfileWallPage = {
    entries: ProfileWallEntry[];
    /** Null means the stream ended. Opaque to the client: never parse it, never build one. */
    nextCursor: string | null;
    viewerIsOwner: boolean;
    showAchievements: boolean;
    showEndorsements: boolean;
};

export type ProfileWallPreferencesRequest = {
    showAchievements?: boolean;
    showEndorsements?: boolean;
};

/**
 * The stable per-entry address, used for the anchor and for the removal call.
 *
 * One function so the anchor the notification deep-link targets and the `id` the entry renders are
 * built by the same code. Two copies of this mapping is how a deep link comes to point at an element
 * that is on the page under a different name, which passes every URL-shaped test in full.
 */
export function profileWallEntryKey(entry: Pick<ProfileWallEntry, "kind" | "sourceId">): string {
    return `${entry.kind}-${entry.sourceId}`;
}
